import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import type { SkillInput } from '@creanizer/types'

interface SkillFormStandardProps {
  input: SkillInput
  onChange: (input: SkillInput) => void
}

export function SkillFormStandard({
  input,
  onChange
}: SkillFormStandardProps) {
  return (
    <div className="space-y-6">
      {/* 基本設定 */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">スキル名（オプション）</Label>
          <Input
            id="name"
            value={input.name || ''}
            onChange={(e) => onChange({ ...input, name: e.target.value })}
            placeholder="例: 火炎斬り"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="category">カテゴリ</Label>
          <Input
            id="category"
            value={input.category || ''}
            onChange={(e) => onChange({ ...input, category: e.target.value })}
            placeholder="例: 剣技、火魔法"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>スキルタイプ</Label>
          <Select
            value={input.skillType || 'active'}
            onValueChange={(value: any) => onChange({ ...input, skillType: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="physical">物理</SelectItem>
              <SelectItem value="magical">魔法</SelectItem>
              <SelectItem value="mental">精神</SelectItem>
              <SelectItem value="social">社交</SelectItem>
              <SelectItem value="special">特殊</SelectItem>
              <SelectItem value="passive">パッシブ</SelectItem>
              <SelectItem value="active">アクティブ</SelectItem>
              <SelectItem value="ultimate">究極</SelectItem>
              <SelectItem value="racial">種族</SelectItem>
              <SelectItem value="class_specific">クラス専用</SelectItem>
              <SelectItem value="item_granted">アイテム付与</SelectItem>
              <SelectItem value="hybrid">ハイブリッド</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {input.skillType === 'custom' && (
          <div className="space-y-2">
            <Label htmlFor="customType">カスタムタイプ</Label>
            <Input
              id="customType"
              value={input.customType || ''}
              onChange={(e) => onChange({ ...input, customType: e.target.value })}
              placeholder="独自のタイプを入力"
            />
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>階層</Label>
          <Select
            value={input.tier || 'basic'}
            onValueChange={(value: any) => onChange({ ...input, tier: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">基本</SelectItem>
              <SelectItem value="intermediate">中級</SelectItem>
              <SelectItem value="advanced">上級</SelectItem>
              <SelectItem value="master">皆伝</SelectItem>
              <SelectItem value="hero">英雄</SelectItem>
              <SelectItem value="legendary">伝説</SelectItem>
              <SelectItem value="mythic">神話</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>レアリティ</Label>
          <Select
            value={input.rarity || 'common'}
            onValueChange={(value: any) => onChange({ ...input, rarity: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="common">コモン</SelectItem>
              <SelectItem value="uncommon">アンコモン</SelectItem>
              <SelectItem value="rare">レア</SelectItem>
              <SelectItem value="epic">エピック</SelectItem>
              <SelectItem value="legendary">レジェンダリー</SelectItem>
              <SelectItem value="mythic">ミシック</SelectItem>
              <SelectItem value="unique">ユニーク</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>スキルの性質</Label>
          <Select
            value={input.skillNature || 'auto'}
            onValueChange={(value: any) => onChange({ ...input, skillNature: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">おまかせ（魔法・技術混合）</SelectItem>
              <SelectItem value="magical">魔法・異能のみ</SelectItem>
              <SelectItem value="technical">純技術のみ</SelectItem>
              <SelectItem value="hybrid">意図的な融合型</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            スキルの発動原理を指定できます
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="level">習得レベル（Lv）</Label>
          <Input
            id="level"
            type="number"
            value={input.level || ''}
            onChange={(e) => onChange({ ...input, level: parseInt(e.target.value) || undefined })}
            placeholder="自動"
            min={1}
            max={100}
          />
          <p className="text-xs text-muted-foreground">
            空欄の場合は階層に応じて自動設定（1-100）
          </p>
        </div>
      </div>
      
      {/* 詳細設定 */}
      <Accordion type="single" collapsible>
        <AccordionItem value="advanced">
          <AccordionTrigger>詳細設定</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>威力レベル（1-10）</Label>
                <Slider
                  value={[input.powerLevel || 5]}
                  onValueChange={([value]) => onChange({ ...input, powerLevel: value })}
                  max={10}
                  min={1}
                  step={1}
                />
                <p className="text-sm text-muted-foreground">
                  現在値: {input.powerLevel || 5}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>複雑さ（1-10）</Label>
                <Slider
                  value={[input.complexity || 5]}
                  onValueChange={([value]) => onChange({ ...input, complexity: value })}
                  max={10}
                  min={1}
                  step={1}
                />
                <p className="text-sm text-muted-foreground">
                  現在値: {input.complexity || 5} - 高いほど使用が難しい
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>習得難易度（1-10）</Label>
                <Slider
                  value={[input.masteryDifficulty || 5]}
                  onValueChange={([value]) => onChange({ ...input, masteryDifficulty: value })}
                  max={10}
                  min={1}
                  step={1}
                />
                <p className="text-sm text-muted-foreground">
                  現在値: {input.masteryDifficulty || 5}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>視覚スタイル</Label>
                <Select
                  value={input.visualStyle || 'realistic'}
                  onValueChange={(value: any) => onChange({ ...input, visualStyle: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realistic">リアリスティック</SelectItem>
                    <SelectItem value="flashy">派手</SelectItem>
                    <SelectItem value="subtle">控えめ</SelectItem>
                    <SelectItem value="ethereal">幻想的</SelectItem>
                    <SelectItem value="brutal">暴力的</SelectItem>
                    <SelectItem value="elegant">優雅</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="elementalAffinity">属性</Label>
                <Input
                  id="elementalAffinity"
                  value={input.elementalAffinity || ''}
                  onChange={(e) => onChange({ ...input, elementalAffinity: e.target.value })}
                  placeholder="例: 火、水、雷、闇"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeVariations"
                  checked={input.includeVariations || false}
                  onCheckedChange={(checked) => 
                    onChange({ ...input, includeVariations: checked as boolean })
                  }
                />
                <Label htmlFor="includeVariations">
                  バリエーションを生成（属性違い、クラス専用版等）
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeCounterSkills"
                  checked={input.includeCounterSkills || false}
                  onCheckedChange={(checked) => 
                    onChange({ ...input, includeCounterSkills: checked as boolean })
                  }
                />
                <Label htmlFor="includeCounterSkills">
                  対抗手段を生成（カウンター、防御方法等）
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label>創造性レベル（1-10）</Label>
                <Slider
                  value={[input.creativityLevel || 7]}
                  onValueChange={([value]) => onChange({ ...input, creativityLevel: value })}
                  max={10}
                  min={1}
                  step={1}
                />
                <p className="text-sm text-muted-foreground">
                  現在値: {input.creativityLevel || 7}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="additionalSettings">追加設定</Label>
                <Textarea
                  id="additionalSettings"
                  value={input.additionalSettings || ''}
                  onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
                  placeholder="その他の要望や設定を入力..."
                  rows={3}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}